import * as React from "react";
import { fetchStreets, IStreet } from "@nju/data-access-portal-wwt";
import { Combobox } from "@nju/form";
import { Loader } from "@nju/ui";

export function StreetAutocomplete({
  cityId,
  postCode,
  isDisabled,
  onChange,
  state,
  defaultValue,
  resetEnabled = true,
}: {
  cityId?: number;
  postCode?: string;
  isDisabled?: boolean;
  onChange: (item?: IStreet) => void;
  state?: "error";
  defaultValue?: string;
  resetEnabled?: boolean;
}) {
  const [inputValue, setInputValue] = React.useState("");
  const [isLoading, setIsLoading] = React.useState(false);
  const [data, setData] = React.useState<IStreet[]>([]);
  const [reset, setReset] = React.useState(false);

  React.useEffect(() => {
    if (!cityId || !resetEnabled) {
      return;
    }

    setReset(true);
  }, [cityId, postCode, isDisabled, resetEnabled]);

  React.useEffect(() => {
    if (!reset) {
      return;
    }

    setInputValue("");
    setData([]);
  }, [reset]);

  React.useEffect(() => {
    async function action(signal: AbortSignal) {
      if (inputValue.length < 3 || !cityId) {
        return;
      }
      try {
        setIsLoading(true);
        const list = (
          await fetchStreets({ query: inputValue, cityId, postCode, signal })
        ).unwrap();

        setData(list);
      } catch {
        setIsLoading(false);
      } finally {
        setIsLoading(false);
      }
    }

    const abortController = new AbortController();

    action(abortController.signal);

    return () => {
      abortController.abort();
    };
  }, [cityId, inputValue, postCode]);

  return (
    <Combobox
      defaultValue={
        defaultValue ? [{ name: defaultValue } as IStreet] : undefined
      }
      inputDisplayValue={([v]) => {
        return v?.name || "";
      }}
      isDisabled={isDisabled}
      itemComparator={(first, second) => {
        return first.id === second.id;
      }}
      items={data}
      key={defaultValue}
      label="ulica"
      listColorVariant="gray"
      listItemDisplayValue={(v) => {
        return v.name;
      }}
      onInputChange={setInputValue}
      onSelect={([element]) => {
        if (element) {
          onChange(element);
        }
      }}
      openOnFocus={false}
      reset={{
        enabled: reset,
        setReset,
      }}
      state={state}
      suffix={isLoading ? <Loader /> : undefined}
      uniqueItemId={(item) => {
        return item.id.toString();
      }}
    />
  );
}
